import React from 'react';
import { FaShieldAlt, FaUsers, FaAward, FaHeart } from 'react-icons/fa';
import SEO from '../components/SEO';

const About: React.FC = () => {
  const values = [
    {
      icon: FaShieldAlt,
      title: 'Transparency',
      description: 'We provide honest, unbiased reviews based on real player experiences and thorough testing.'
    },
    {
      icon: FaUsers,
      title: 'Player First',
      description: 'Every review is written with players in mind, helping you make informed decisions.'
    },
    {
      icon: FaAward,
      title: 'Excellence',
      description: 'We only feature casinos that meet our strict standards for quality, security, and fairness.'
    },
    {
      icon: FaHeart,
      title: 'Responsible Gaming',
      description: 'We promote safe gambling practices and support responsible gaming initiatives.'
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About ReelVindicator",
    "description": "Learn about ReelVindicator - your trusted source for UK casino reviews and expert gaming advice."
  };

  return (
    <>
      <SEO
        title="About Us - Your Trusted UK Casino Review Source"
        description="Learn about ReelVindicator's mission to provide honest, expert reviews of UK online casinos. We help players make informed decisions."
        schema={schema}
      />

      <div className="pt-20 pb-12">
        {/* Hero */}
        <section className="py-12 bg-gradient-to-br from-dark-surface to-dark-bg">
          <div className="container mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold text-center mb-4">
              <span className="text-gradient">About ReelVindicator</span>
            </h1>
            <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto">
              Your trusted source for honest, expert UK casino reviews
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="glass p-8 rounded-xl">
              <h2 className="text-4xl font-bold mb-6">
                <span className="text-gradient">Our Mission</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                At ReelVindicator, we're dedicated to helping UK players navigate the vast world of online casinos. 
                With hundreds of options available, finding the right casino can be overwhelming. That's where we come in.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Our team of gaming experts spends countless hours testing casinos, analyzing bonuses, reviewing games, 
                and evaluating customer support. We test everything from sign-up processes to withdrawal times, ensuring 
                our reviews reflect the real player experience.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                We believe that every player deserves access to accurate, unbiased information. That's why we never 
                accept payment for positive reviews and always disclose any partnerships or affiliate relationships. 
                Your trust is our most valuable asset.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-12">
              <span className="text-gradient">Our Values</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="glass p-6 rounded-xl text-center hover:border-accent-primary transition-all">
                  <value.icon className="text-5xl text-accent-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-gray-400">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How We Review */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-4xl font-bold text-center mb-12">
              <span className="text-gradient">How We Review Casinos</span>
            </h2>
            <div className="space-y-6">
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-3">1. Registration & Verification</h3>
                <p className="text-gray-400">
                  We start by creating an account and going through the verification process. 
                  We note how long it takes, what documents are required, and how user-friendly the process is.
                </p>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-3">2. Game Testing</h3>
                <p className="text-gray-400">
                  We test a wide variety of games across different categories - slots, table games, live casino, and more. 
                  We evaluate game quality, variety, and the overall gaming experience.
                </p>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-3">3. Bonus Analysis</h3>
                <p className="text-gray-400">
                  We carefully examine all bonus terms and conditions, including wagering requirements, 
                  maximum bet limits, game restrictions, and withdrawal conditions.
                </p>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-3">4. Payment Testing</h3>
                <p className="text-gray-400">
                  We test deposit and withdrawal processes, noting processing times, fees, 
                  available payment methods, and any restrictions or limits.
                </p>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-3">5. Customer Support</h3>
                <p className="text-gray-400">
                  We test all support channels - live chat, email, and phone - evaluating response times, 
                  helpfulness, and the overall quality of customer service.
                </p>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-3">6. Security & Licensing</h3>
                <p className="text-gray-400">
                  We verify licensing information, check SSL certificates, review privacy policies, 
                  and ensure the casino meets all regulatory requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient">Ready to Find Your Perfect Casino?</span>
            </h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Start exploring our reviews and find the casino that matches your preferences and playing style.
            </p>
            <a
              href="/comparison"
              className="inline-block bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-4 px-8 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all transform hover:scale-105"
            >
              Compare Offers Now
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;

